import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageManagerService {
  private readonly SALES_OFFICE_ID = 'sales-office-id';
  private readonly DSM_SALES_OFFICE_ID = 'dsm-sales-office-id';
  private readonly SALES_LISTING_VIEWS = 'sales-listing-report-views';
  private readonly DSM_ROOM_REPLACEMENT_ACK_EXECUTIONS = 'dsm-room-replacement-ack-executions';
  private readonly RECENT_SCREENS = 'recent-screens';
  private readonly WELCOME_FAVORITES = 'welcome-favorites';
  private readonly WELCOME_VIEW = 'welcome-view';

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  saveUserToStorage(data: any): boolean {
    this.removeUser();
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('user', JSON.stringify(data));
    }
    return true;
  }

  savePermissionsToStorage(data: any): boolean {
    this.removePermissions();
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('permissions', JSON.stringify(data));
    }
    return true;
  }

  getPermissionsFromStorage(): any {
    return JSON.parse(isPlatformBrowser(this.platformId) ? localStorage.getItem('permissions')! : '');
  }

  saveAdminUserToStorage(): void {
    this.removeAdmin();
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('isAdmin', '1');
    }
  }

  updateUserToStorage(data: any): void {
    this.removeUser();
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('user', JSON.stringify(data));
    }
  }

  setOtpEmailToStorage(email: any): void {
    this.removeOtpEmail();
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('otpEmail', email);
    }
  }

  getOtpEmailToStorage(): string {
    return isPlatformBrowser(this.platformId) ? localStorage.getItem('otpEmail')! : '';
  }

  getTokenFromStorage(): string {
    return isPlatformBrowser(this.platformId) ? localStorage.getItem('token') || '{}' : '';
  }

  getRefreshTokenFromStorage(): string {
    return isPlatformBrowser(this.platformId) ? localStorage.getItem('refresh') || '{}' : '';
  }

  setTokenToStorage(authToken: string, refreshToken: string): void {
    this.removeToken();
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('token', authToken);
      localStorage.setItem('refresh', refreshToken);
    }
  }

  removeRefreshToken(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('refresh');
    }
  }

  isLoginValidationFromStorage(): boolean {
    let token = localStorage.getItem('token') || '';
    return token !== '' ? true : false;
  }

  isOtpEmailValidationFromStorage(): boolean {
    let otpEmail = isPlatformBrowser(this.platformId) ? localStorage.getItem('otpEmail') || '{}' : '';
    return otpEmail !== '' ? true : false;
  }

  isAdminValidationFromStorage(): boolean {
    let token = localStorage.getItem('isAdmin') || '';
    return token === '1' ? true : false;
  }

  getUserFromStorage(): any {
    return JSON.parse(isPlatformBrowser(this.platformId) ? localStorage.getItem('user') || '{}' : '');
  }

  getUserIdFromStorage(): string | null {
    let user: any = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.id || null;
  }

  getUserRoleFromStorage(): string {
    let user: any = JSON.parse(isPlatformBrowser(this.platformId) ? localStorage.getItem('user') || '{}' : '');
    return user.role;
  }

  private readonly PERSISTENT_STORAGE_KEY_FRAGMENTS: string[] = [
    this.SALES_OFFICE_ID,
    this.DSM_ROOM_REPLACEMENT_ACK_EXECUTIONS,
    this.WELCOME_FAVORITES,
    this.WELCOME_VIEW
  ];

  clearLocalStorage(): any {
    Object.keys(localStorage).forEach(key => {
      if (!this.PERSISTENT_STORAGE_KEY_FRAGMENTS.some(fragment => key.includes(fragment))) {
        localStorage.removeItem(key);
      }
    });
  }

  removePermissions(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('permissions');
    }
  }

  removeUser(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('user');
    }
  }

  removeAdmin(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('isAdmin');
    }
  }

  removeToken(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh');
    }
  }

  removeOtpEmail(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('otpEmail');
    }
  }

  getOrCreateDeviceId(): string {
    let deviceId = '';
    if (isPlatformBrowser(this.platformId)) {
      deviceId = localStorage.getItem('deviceId') || '';
      if (!deviceId) {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          deviceId = crypto.randomUUID();
        } else {
          deviceId = Math.random().toString(36).substring(2) + Date.now().toString(36);
        }
        localStorage.setItem('deviceId', deviceId);
      }
    }
    return deviceId;
  }

  setSalesOfficeIdToStorage(salesOfficeId: string): void {
    const userId = this.getUserIdFromStorage();
    if (!userId) return;

    const key = `${userId}-${this.SALES_OFFICE_ID}`;
    localStorage.setItem(`${key}`, salesOfficeId);
  }

  getSalesOfficeIdFromStorage(): string | null {
    const userId = this.getUserIdFromStorage();
    if (!userId) return null;
    const key = `${userId}-${this.SALES_OFFICE_ID}`;
    return localStorage.getItem(key) || null;
  }

  setDsmSalesOfficeIdToStorage(salesOfficeId: string): void {
    const userId = this.getUserIdFromStorage();
    if (!userId) return;
    localStorage.setItem(`${userId}-${this.DSM_SALES_OFFICE_ID}`, salesOfficeId);
  }

  getDsmSalesOfficeIdFromStorage(): string | null {
    const userId = this.getUserIdFromStorage();
    if (!userId) return null;
    return localStorage.getItem(`${userId}-${this.DSM_SALES_OFFICE_ID}`) || null;
  }

  setSelectedStatusIdsToStorage(statusIds: string[]): void {
    localStorage.setItem('selectedStatusIds', JSON.stringify(statusIds));
  }

  removeSelectedStatusIds(): void {
    localStorage.removeItem('selectedStatusIds');
  }
  getSelectedStatusIdsFromStorage(): string[] {
    return JSON.parse(localStorage.getItem('selectedStatusIds') || '[]') as string[];
  }

  setSalesListingViewsToStorage(state: unknown): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const userId = this.getUserIdFromStorage();
    if (!userId) return;
    try {
      localStorage.setItem(`${userId}-${this.SALES_LISTING_VIEWS}`, JSON.stringify(state));
    } catch {
      // ignore quota / access errors
    }
  }

  getSalesListingViewsFromStorage<T = unknown>(): T | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const userId = this.getUserIdFromStorage();
    if (!userId) return null;
    const raw = localStorage.getItem(`${userId}-${this.SALES_LISTING_VIEWS}`);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  getAcknowledgedRoomReplacementExecutionIds(source: string): Set<string> {
    if (!isPlatformBrowser(this.platformId)) return new Set();
    const userId = this.getUserIdFromStorage();
    if (!userId) return new Set();
    try {
      const raw = localStorage.getItem(`${userId}-${source}-${this.DSM_ROOM_REPLACEMENT_ACK_EXECUTIONS}`);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  }

  acknowledgeRoomReplacementExecution(executionId: string, source: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const userId = this.getUserIdFromStorage();
    if (!userId) return;
    const ids = this.getAcknowledgedRoomReplacementExecutionIds(source);
    ids.add(executionId);
    try {
      localStorage.setItem(`${userId}-${source}-${this.DSM_ROOM_REPLACEMENT_ACK_EXECUTIONS}`, JSON.stringify([...ids]));
    } catch {
      // ignore quota / access errors
    }
  }

  setAddOptionViewModeToStorage(mode: 'grid' | 'list'): void {
    localStorage.setItem('dsm-add-option-view-mode', mode);
  }

  getAddOptionViewModeFromStorage(): 'grid' | 'list' {
    const saved = localStorage.getItem('dsm-add-option-view-mode');
    return saved === 'list' ? 'list' : 'grid';
  }

  getRecentScreensFromStorage(): string[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    const userId = this.getUserIdFromStorage();
    if (!userId) return [];
    try {
      return JSON.parse(localStorage.getItem(`${userId}-${this.RECENT_SCREENS}`) || '[]') as string[];
    } catch {
      return [];
    }
  }

  /** Prepend path for its module segment; keep at most `max` entries. No-ops if unchanged. */
  upsertRecentScreen(path: string, segment: string, max = 4): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const userId = this.getUserIdFromStorage();
    if (!userId) return;

    const key = `${userId}-${this.RECENT_SCREENS}`;
    let previous: string[] = [];
    try {
      previous = JSON.parse(localStorage.getItem(key) || '[]') as string[];
    } catch {
      previous = [];
    }

    const next = [path, ...previous.filter(p => p.split('/').filter(Boolean)[0] !== segment)].slice(0, max);
    if (next.length === previous.length && next.every((p, i) => p === previous[i])) return;

    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // ignore quota / access errors
    }
  }

  /** Module routes the user starred on the welcome screen. */
  getWelcomeFavoritesFromStorage(): string[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    const userId = this.getUserIdFromStorage();
    if (!userId) return [];
    try {
      return JSON.parse(localStorage.getItem(`${userId}-${this.WELCOME_FAVORITES}`) || '[]') as string[];
    } catch {
      return [];
    }
  }

  setWelcomeFavoritesToStorage(routes: string[]): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const userId = this.getUserIdFromStorage();
    if (!userId) return;
    try {
      localStorage.setItem(`${userId}-${this.WELCOME_FAVORITES}`, JSON.stringify(routes));
    } catch {
      // ignore quota / access errors
    }
  }

  /** Whether the welcome screen is showing all modules or favorites only. */
  getWelcomeViewFromStorage(): 'home' | 'favorites' {
    if (!isPlatformBrowser(this.platformId)) return 'home';
    const userId = this.getUserIdFromStorage();
    if (!userId) return 'home';
    return localStorage.getItem(`${userId}-${this.WELCOME_VIEW}`) === 'favorites' ? 'favorites' : 'home';
  }

  setWelcomeViewToStorage(view: 'home' | 'favorites'): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const userId = this.getUserIdFromStorage();
    if (!userId) return;
    try {
      localStorage.setItem(`${userId}-${this.WELCOME_VIEW}`, view);
    } catch {
      // ignore quota / access errors
    }
  }
}
